// Copyright (c) StrangeBee 2024

import { useCallback, useState } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import { ColumnType } from 'antd/lib/table';
import { THList } from '@Molecules/THList';
import { AttachmentCaseType, AttachmentType, ObjectTypeV1 } from 'thehive-sdk';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ICONS } from '@Constants/iconsApp';
import { formatFileSize } from '@Helpers/stats';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { copyToClipboard } from '@Helpers/copy';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { useDrawer } from '@Hooks/useDrawer';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { AttachmentsCreate } from '@Organisms/Attachments/AttachmentsCreate/AttachmentsCreate';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { computeEventList } from '@Organisms/Case/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { AttachmentListProps } from '../types';
import usePageTitle from '@Hooks/usePageTitle';

export const AttachmentCasesList = ({
    describe,
    parentId,
    parentNumber,
    previewManager,
    hasPermissions = false,
}: AttachmentListProps) => {
    const i = useLocalMessage();
    usePageTitle(i('page-title.cases.case-attachments', { number: parentNumber || 0 }));

    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<AttachmentType>({});

    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(false);

    const {
        filterManager,
        storagePageManager,
        dataSource,
        columnsRenderer,
        paginationManager,
        table,
        listRefetch,
        countRefetch,
        status = 'loading',
    } = useConfigList<AttachmentCaseType>({
        selector: {
            head: [{ name: 'getCase', idOrName: parentId }, { name: 'attachments' }],
            name: 'get-case-attachments',
        },
        controller: client.CaseController,
        storageId: StorageFilterEnum.CASE_ATTACHMENTS,
        objectType: ObjectTypeV1.CASE,
        streamRootId: parentId,
    });

    const eventList = computeEventList({
        controller: client.CaseController,
        locale: i,
        onError: handleNotificationError,
        onSuccess: (_, msg) => {
            openMessage('success', msg);
            listRefetch();
            countRefetch();
        },
    });

    const getDownloadUrl = useCallback(
        (attachmentId: string): string =>
            window.location.origin + client.apiUrl + `/v1/case/${parentId}/attachment/${attachmentId}/download`,
        [parentId],
    );

    const setLocalFilters = useCallback(
        (localFilters: boolean): void => {
            setLocalFilterIsOpen(localFilters);
            storagePageManager?.setFilterIsOpen?.(localFilters);
            filterManager.setEditFilters(localFilters);
        },
        [filterManager, storagePageManager],
    );

    const handleDelete = useCallback(
        (attachmentId: string) => {
            eventList.attachment
                .onDelete(parentId, attachmentId)
                .then(() => {
                    openMessage('success', i('attachment.action.success.delete'));
                    listRefetch();
                })
                .catch(handleNotificationError);
        },
        [parentId, eventList.attachment, i, listRefetch],
    );

    const getColumns = useCallback(
        (): ColumnType<AttachmentCaseType>[] =>
            columnsRenderer([
                {
                    title: [
                        {
                            label: i('case.attachment.table.filename'),
                            sortField: 'name',
                        },
                    ],
                    className: 'case-attachment-list__column-filename custom-table__cell',
                    render: (attachement) => {
                        const { name, contentType } = attachement;

                        return previewManager.allowPreview(contentType) ? (
                            <THButton
                                type="link"
                                onClick={() =>
                                    previewManager.togglePreview(attachement, { type: 'case', id: parentId })
                                }
                            >
                                {name}
                            </THButton>
                        ) : (
                            <span>{name}</span>
                        );
                    },
                },
                {
                    title: [
                        {
                            label: i('case.attachment.table.type'),
                            sortField: 'contentType',
                        },
                    ],
                    className: 'case-attachment-list__column-type custom-table__cell',
                    render: ({ contentType }) => <span>{contentType}</span>,
                },
                {
                    title: [
                        {
                            label: i('case.attachment.table.size'),
                            sortField: 'size',
                        },
                    ],
                    className: 'case-attachment-list__column-size custom-table__cell',
                    render: ({ size }) => <span>{formatFileSize(size)}</span>,
                },
                {
                    title: [
                        {
                            label: i('case.attachment.table.date'),
                            sortField: '_createdAt',
                        },
                    ],
                    className: 'case-attachment-list__column-date custom-table__cell',
                    render: ({ _createdAt = 0 }) => <FormatDate date={_createdAt} />,
                },
                {
                    className: 'case-attachment-list__column-download custom-table__cell',
                    render: ({ _id }) => (
                        <ActionsList
                            actionList={{
                                copy: {
                                    label: i('general.copy-url'),
                                    icon: <ICONS.CopyOutlined />,
                                    query: () => copyToClipboard(getDownloadUrl(_id), i('notification.copy')),
                                },
                                export: {
                                    label: i('general.download'),
                                    icon: <ICONS.DownloadOutlined />,
                                    CustomComponent: (triggerElem) => (
                                        <ExternalLink url={getDownloadUrl(_id)}>{triggerElem}</ExternalLink>
                                    ),
                                },
                                delete: {
                                    label: i('general.remove'),
                                    message: i('attachment.action.confirm.delete'),
                                    hidden: !hasPermissions,
                                    icon: <ICONS.DeleteIcon />,
                                    confirm: true,
                                    query: () => {
                                        handleDelete(_id);
                                    },
                                },
                            }}
                            scope="single"
                            isIcon
                        />
                    ),
                },
            ]),
        [columnsRenderer, i, previewManager, parentId, hasPermissions, getDownloadUrl, handleDelete],
    );

    return (
        <>
            <THList>
                <THList.Header className="case-attachment-header">
                    <THList.Slot name="MANAGE">
                        {/* Add Attachment Button */}
                        {hasPermissions && parentId && (
                            <AddButton
                                label={i('attachment.sider.add')}
                                className="case-attachment-header__btn-add"
                                onClick={() => setVisibleCreate(true)}
                            />
                        )}
                    </THList.Slot>
                    <THList.Slot name="MANAGE">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={localFilterIsOpen}
                            onChange={setLocalFilters}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectTypeV1.ATTACHMENT]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    columns={getColumns()}
                    rowKey="_id"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('case.attachment.none')} />
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {visibleCreate && (
                <AttachmentsCreate
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onAddAttachment={(formData) => eventList.attachment.onAdd(parentId, formData)}
                />
            )}
        </>
    );
};
